import {
    Component,
    h,
    Prop,
    Host,
    Element,
    Event,
    EventEmitter,
    State,
    Watch,
} from "@stencil/core";
import { languages } from "./languages";

@Component({
    tag: "ux-iws-sf-language-switch",
    styleUrl: "sf-language-switch.scss",
    shadow: true,
})
export class SfLanguageSwitch {
    @Element() domNode: HTMLUxIwsSfLanguageSwitchElement;
    /** The currently selected language. */
    @Prop() language: string = "";
    /** Whether the language switch is opened. */
    @Prop({ reflect: true }) opened: boolean = false;
    /** id of a target element that should be used for positioning */
    @Prop() targetId: string = "";

    @State() selectedLanguage: string;

    /** is emitted when a language is selected */
    @Event() uxChangeLanguage: EventEmitter<{ language: string }>;

    /** is emitted when the language switch should be closed */
    @Event() uxClose: EventEmitter<void>;

    private setLanguage(language: string) {
        this.selectedLanguage = language;
        this.uxChangeLanguage.emit({ language });
    }

    @Watch("language")
    watchLanguage(newValue: string) {
        this.selectedLanguage = newValue || languages[0].id;
    }

    @Watch("opened")
    watchOpened() {
        if (this.opened) {
            this.calculatePosition();
            this.domNode.querySelector("button")?.focus();
            document.body.addEventListener("click", this.handleBodyClick);
        } else {
            document.body.removeEventListener("click", this.handleBodyClick);
        }
    }

    // the switch should be centered below the target element.
    // We need to make sure that the switch is not positioned off-screen or cut off
    // The switch must be position:fixed
    private calculatePosition = () => {
        if (!this.targetId) return;
        const targetElement = document.getElementById(this.targetId);
        if (!targetElement) return;

        const { bottom, left } = targetElement.getBoundingClientRect();
        const { innerWidth } = window;
        const marginTop = 5;

        const isOverflowingRight =
            left + targetElement.offsetWidth > innerWidth;
        const isOverflowingLeft = left < 0;
        const centered =
            left + targetElement.offsetWidth / 2 - this.domNode.offsetWidth / 2;
        const overflowingRightPosition = innerWidth - this.domNode.offsetWidth;

        this.domNode.style.top = `${bottom + marginTop}px`;
        this.domNode.style.left = `${isOverflowingRight ? overflowingRightPosition : isOverflowingLeft ? 0 : centered}px`;
    };

    disconnectedCallback() {
        document.body.removeEventListener("click", this.handleBodyClick);
    }

    private handleBodyClick = (event: UIEvent): void => {
        if (
            !this.opened ||
            this.domNode === event.target ||
            this.domNode.contains(event.target as Node)
        ) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        this.uxClose.emit();
    };

    componentWillLoad() {
        this.selectedLanguage = this.language || languages[0].id;
    }

    render() {
        return (
            <Host inert={!this.opened} aria-hidden={!this.opened}>
                <div class="list">
                    {languages.map((lang) => (
                        <button
                            type="button"
                            class={{
                                option: true,
                                "is-selected":
                                    lang.id === this.selectedLanguage,
                            }}
                            key={lang.id}
                            onClick={() => this.setLanguage(lang.id)}
                        >
                            {lang.label}
                        </button>
                    ))}
                </div>
            </Host>
        );
    }
}
